import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/["']/g, '').replace(/\/+$/, '');

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const baseUrl = API_BASE.replace(/\/api\/?$/, '');
  return `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
};

function MathText({ children }) {
  if (!children) return null;
  const regex = /(\d+\/\d+|___+|\\underline\{\\hspace\{[^}]+\}\}|underlinehspace\w+|underlinephantom\w+)/g;
  const parts = children.toString().split(regex);
  
  return (
    <span style={{ fontFamily: 'inherit', fontSize: '1.05em', whiteSpace: 'pre-wrap' }}>
      {parts.map((part, i) => {
        if (!part) return null;
        const t = part.trim();
        
        if (/^\d+\/\d+$/.test(t)) {
          const [n, d] = t.split('/');
          return (
            <span 
              key={i} 
              dangerouslySetInnerHTML={{ 
                __html: katex.renderToString(`\\frac{${n}}{${d}}`, { throwOnError: false }) 
              }} 
              style={{ margin: '0 2px', display: 'inline-block' }}
            />
          );
        } 
        else if (/^[a-zA-Z]+\/\d+$/.test(t)) {
          const [num, den] = t.split('/');
          return (
            <span 
              key={i} 
              dangerouslySetInnerHTML={{ 
                __html: katex.renderToString(`\\frac{${num}}{${den}}`, { throwOnError: false }) 
              }} 
              style={{ margin: '0 2px', display: 'inline-block' }}
            />
          );
        }
        else if (/^(\d+|[a-zA-Z])\^[0-9]+$/.test(t)) {
          const [base, exp] = t.split('^');
          return (
            <span 
              key={i} 
              dangerouslySetInnerHTML={{ 
                __html: katex.renderToString(`{${base}}^{${exp}}`, { throwOnError: false }) 
              }} 
              style={{ margin: '0 2px', display: 'inline-block' }}
            />
          );
        }
        else if (/^___+$/.test(t) || /^\\?underline\{\\?hspace\{[^}]+\}\}$/.test(t) || /^underlinehspace\w+$/i.test(t) || /^underlinephantom\w+$/i.test(t)) {
          return (
            <span 
              key={i} 
              dangerouslySetInnerHTML={{ 
                __html: katex.renderToString("\\underline{\\hspace{1cm}}", { throwOnError: false }) 
              }} 
              style={{ margin: '0 4px', display: 'inline-block' }}
            />
          );
        }
        
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

const COMPETENCY_LEVELS = [
  { name: 'foundational', color: '#6c757d', minScore: 0, maxScore: 30 },
  { name: 'emerging', color: '#dc3545', minScore: 31, maxScore: 50 },
  { name: 'developing', color: '#ffc107', minScore: 51, maxScore: 70 },
  { name: 'proficient', color: '#007BFF', minScore: 71, maxScore: 85 },
  { name: 'advanced', color: '#28a745', minScore: 86, maxScore: 100 }
];

const getCompetencyWord = (score) => {
  if (score >= 86) return 'Advanced';
  if (score >= 71) return 'Proficient';
  if (score >= 51) return 'Developing';
  if (score >= 31) return 'Emerging';
  return 'Foundational';
};

const getCompetencyColor = (score) => {
  if (score >= 86) return '#28a745';
  if (score >= 71) return '#007BFF';
  if (score >= 51) return '#ffc107';
  if (score >= 31) return '#dc3545';
  return '#6c757d';
};

const translations = {
  en: {
    back: '← Back', student: 'Student', school: 'School', centerId: 'Center ID', subject: 'Subject', status: 'Status',
    totalQuestions: 'Total Questions', unique: 'unique', duration: 'Duration', completed: 'Completed', inProgress: 'In Progress', grade: 'Grade',
    overallMath: 'Overall math competency', foundational: 'Foundational', emerging: 'Emerging', developing: 'Developing', proficient: 'Proficient', advanced: 'Advanced',
    misconceptions: '🧠 Misconceptions', answerTag: 'Misconception', count: 'Count', percentErrors: '% of All Errors',
    questionTagAnalysis: '📊 Skill Analysis', tag: 'Skill / Focus Area', accuracy: 'Accuracy', performance: 'Performance',
    chaptersGlance: 'Chapters at a Glance', core: 'Core', overallEnglish: 'Overall English competency',
    overallAccuracy: 'Overall accuracy based on {correct} correct out of {total} total questions attempted.',
    pass: 'Pass', fail: 'Fail', originalScore: 'Original Score', backupScore: 'Backup Score', totalBandAccuracy: 'Total Band Accuracy',
    tagCombosAttempted: 'Skills Assessed:', uniqueTagCombos: '🏷️ Unique Skill Combinations', tagCombination: 'Skill Combination',
    performanceDashboard: '📊 Performance Dashboard', byConcept: '(By Concept)', byBand: '(By Band)',
    questionAudit: '🔍 Question Audit ({count} Questions)', noAnswers: 'No answers recorded for this attempt.',
    wrong: 'Wrong', difficulty: 'Difficulty', type: 'Type', tags: 'Tags', subConcept: 'Sub-Concept', skillsets: 'Skillsets',
    misconceptionsSmall: 'Misconceptions', howResultReached: 'How the result was reached',
    accuracyByConcept: 'Accuracy by concept (best → weakest)', competencyContribution: 'Competency — how each chapter contributes',
    placement: 'Placement (0–80)', accuracyScore: 'Accuracy (0–20)', backToOverview: '← Back to overview',
    noMisconceptions: 'No misconceptions recorded for this chapter.', answerTagMisconception: 'Misconceptions',
    percentChapterErrors: '% of Chapter Errors', questionByQuestion: '📝 Question-by-Question Review',
    correctAnswer: 'Correct Answer', studentSelected: 'Student Selected', notAnswered: 'Not answered', incorrect: 'Incorrect',
    performanceByBand: 'Performance by Band', band: 'Band', none: 'N/A', competencyLevel: 'Competency',
    'Recognising Similar Meaning': 'Recognising Similar Meaning', 'Understanding Question Requirements': 'Understanding Question Requirements',
    'Understanding Sentence Structure': 'Understanding Sentence Structure', 'Making Logical Conclusions': 'Making Logical Conclusions',
    'Understanding Word Meaning': 'Understanding Word Meaning', 'Grammar Accuracy': 'Grammar Accuracy',
    'Finding Key Information': 'Finding Key Information', 'Connecting Ideas Across Text': 'Connecting Ideas Across Text',
    'Understanding Deeper Meaning': 'Understanding Deeper Meaning',
    'Concept Misunderstanding': 'Concept Misunderstanding', 'Step Sequencing': 'Step Sequencing',
    'Calculation Error': 'Calculation Error', 'Instruction Misinterpretation': 'Instruction Misinterpretation',
    'Vocabulary Misunderstanding': 'Vocabulary Misunderstanding',
    'Fundamental Concept': 'Fundamental Concept', 'Multi-Step Problem Solving': 'Multi-Step Problem Solving',
    'Word Problem': 'Word Problem', 'Math Representation': 'Math Representation', 'Logical Reasoning': 'Logical Reasoning'
  },
  cn: {
    back: '← 返回', student: '学生', school: '学校', centerId: '中心 ID', subject: '科目', status: '状态',
    totalQuestions: '总题数', unique: '不重复', duration: '用时', completed: '完成时间', inProgress: '进行中', grade: '年级',
    overallMath: '整体数学能力', foundational: '基础', emerging: '初级', developing: '发展', proficient: '熟练', advanced: '高级',
    misconceptions: '🧠 误区分析', answerTag: '误区类型', count: '数量', percentErrors: '占所有错误的百分比',
    questionTagAnalysis: '📊 技能分析', tag: '技能 / 考察重点', accuracy: '准确率', performance: '表现',
    chaptersGlance: '章节概览', core: '核心', overallEnglish: '整体英语能力',
    overallAccuracy: '基于 {correct} 道正确题，共 {total} 道题的整体准确率。',
    pass: '通过', fail: '未通过', originalScore: '原始分数', backupScore: '备用分数', totalBandAccuracy: '等级总准确率',
    tagCombosAttempted: '考察的技能：', uniqueTagCombos: '🏷️ 唯一技能组合', tagCombination: '技能组合',
    performanceDashboard: '📊 表现仪表盘', byConcept: '（按概念）', byBand: '（按等级）',
    questionAudit: '🔍 问题审查 ({count} 题)', noAnswers: '本次尝试未记录答案。',
    wrong: '错误', difficulty: '难度', type: '类型', tags: '标签', subConcept: '子概念', skillsets: '技能集',
    misconceptionsSmall: '误区', howResultReached: '结果是如何得出的',
    accuracyByConcept: '概念准确率（从最好到最弱）', competencyContribution: '能力评估 — 每个章节的贡献',
    placement: '定位 (0–80)', accuracyScore: '准确率 (0–20)', backToOverview: '← 返回概览',
    noMisconceptions: '本章未记录误区。', answerTagMisconception: '误区分析',
    percentChapterErrors: '占本章错误的百分比', questionByQuestion: '📝 逐题回顾',
    correctAnswer: '正确答案', studentSelected: '学生选择', notAnswered: '未作答', incorrect: '错误',
    performanceByBand: '📈 按等级表现', band: '等级', none: '无', competencyLevel: '能力等级',
    'Recognising Similar Meaning': '语义辨析能力', 'Understanding Question Requirements': '题目理解能力',
    'Understanding Sentence Structure': '语法分析能力', 'Making Logical Conclusions': '逻辑推理能力',
    'Understanding Word Meaning': '词义理解能力', 'Grammar Accuracy': '语法运用能力',
    'Finding Key Information': '关键信息提取能力', 'Connecting Ideas Across Text': '文本信息整合能力',
    'Understanding Deeper Meaning': '深层语义理解能力',
    'Concept Misunderstanding': '概念理解偏差', 'Step Sequencing': '解题步骤组织偏差',
    'Calculation Error': '运算准确性偏差', 'Instruction Misinterpretation': '题目指令理解偏差',
    'Vocabulary Misunderstanding': '数学术语理解偏差',
    'Fundamental Concept': '基础概念掌握', 'Multi-Step Problem Solving': '多步骤问题解决能力',
    'Word Problem': '文字题理解与应用能力', 'Math Representation': '数学表征能力', 'Logical Reasoning': '数学逻辑推理能力'
  }
};

export default function ReviewAttemptDetails({ initialData = null, isParentView = false }) {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState('');
  const [expandedQ, setExpandedQ] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [lang, setLang] = useState('en'); 
  const [tagDefs, setTagDefs] = useState([]); 

  const t = (key, replacements = {}) => {
    let text = translations[lang][key] || key;
    Object.keys(replacements).forEach(k => { text = text.replace(`{${k}}`, replacements[k]); });
    return text;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')} ${String(date.getMonth() + 1).padStart(2, '0')} ${date.getFullYear()}`; 
  };

  const getTagMeaning = (code, tagType) => {
    if (!code || tagDefs.length === 0) return code;
    const subject = data?.assignment?.exam_topic?.toLowerCase(); 
    const def = tagDefs.find(t => t.subject?.toLowerCase() === subject && t.tag_type?.toLowerCase() === tagType?.toLowerCase() && t.code === code);
    if (!def) return code;
    return lang === 'cn' ? def.chinese_meaning : def.english_meaning;
  };

  const getComboMeaning = (comboString) => {
    if (!comboString) return '';
    return comboString.split(' + ').map(code => getTagMeaning(code, 'question')).join(' + ');
  };

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  })();

  const role = user.role || 'admin';
  const isParent = isParentView || role === 'parent';

  useEffect(() => {
    if (!initialData) {
      fetchDetails();
    } else {
      setData(initialData);
      setLoading(false);
      setError('');
    }

    fetchTagDefs();
  }, [assignmentId, initialData]);

  const fetchDetails = async () => {
    try {
      const endpoint =
        role === 'parent'
          ? `${API_BASE}/parent/review/attempts/${assignmentId}/details`
          : role === 'tutor'
          ? `${API_BASE}/tutor/review/attempts/${assignmentId}/details`
          : `${API_BASE}/admin/review/attempts/${assignmentId}/details`;
        
      const res = await fetch(endpoint, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (!res.ok) { const errText = await res.text(); throw new Error(`Failed to fetch: ${res.status} - ${errText}`); }
      const result = await res.json();
      if (!result || !result.assignment) throw new Error('Invalid response from server.');
      setData(result);
    } catch (err) {
      console.error('❌ Fetch error:', err);
      setError(err.message || 'Failed to fetch attempt details');
    } finally { setLoading(false); }
  };

  const fetchTagDefs = async () => {
    try {
      const tagEndpoint = role === 'tutor'
        ? `${API_BASE}/tutor/tag-definitions`
        : `${API_BASE}/admin/tag-definitions`;

      const res = await fetch(tagEndpoint, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const data = await res.json(); setTagDefs(data); }
    } catch (err) { console.error('Failed to fetch tag definitions', err); }
  };

  const uniqueQuestionCount = useMemo(() => {
    if (!data || !data.answers) return 0;
    return new Set(data.answers.map(a => a.question_id)).size;
  }, [data]);

  const isEnglish = data?.assignment?.exam_topic === 'english';
  const isMath = data?.isMath || data?.assignment?.exam_topic === 'math';
  const isScience = data?.isScience || data?.assignment?.exam_topic === 'science';

  const mathData = useMemo(() => {
    if (!data || !isMath || !data.answers || data.answers.length === 0) return null;
    const { answers, sessionData } = data;
    const chapterData = {};
    
    answers.forEach(ans => {
      const chapter = ans.metadata?.chapter || 'Unknown';
      if (!chapterData[chapter]) {
        chapterData[chapter] = {
          core_basic: { correct: 0, total: 0 }, core_intermediate: { correct: 0, total: 0 }, core_advanced: { correct: 0, total: 0 },
          backup_basic: { correct: 0, total: 0 }, backup_intermediate: { correct: 0, total: 0 },
          supporting_basic: { correct: 0, total: 0 }, supporting_intermediate: { correct: 0, total: 0 }, supporting_advanced: { correct: 0, total: 0 },
          concepts: {}, skillTags: {}, supportingConcepts: {}, chosenAnswerTags: {}
        };
      }

      const stage = ans.stage || 'unknown';
      const diff = (ans.metadata?.difficulty || 'basic').toLowerCase();
      const concept = ans.metadata?.concept || 'Unknown';

      if (stage === 'core') {
        if (diff === 'basic') { chapterData[chapter].core_basic.total++; if (ans.is_correct) chapterData[chapter].core_basic.correct++; }
        else if (diff === 'intermediate') { chapterData[chapter].core_intermediate.total++; if (ans.is_correct) chapterData[chapter].core_intermediate.correct++; }
        else if (diff === 'advanced') { chapterData[chapter].core_advanced.total++; if (ans.is_correct) chapterData[chapter].core_advanced.correct++; }
      } else if (stage === 'backup') {
        if (diff === 'basic') { chapterData[chapter].backup_basic.total++; if (ans.is_correct) chapterData[chapter].backup_basic.correct++; }
        else if (diff === 'intermediate') { chapterData[chapter].backup_intermediate.total++; if (ans.is_correct) chapterData[chapter].backup_intermediate.correct++; }
      } else if (stage === 'supporting') {
        if (diff === 'basic') { chapterData[chapter].supporting_basic.total++; if (ans.is_correct) chapterData[chapter].supporting_basic.correct++; }
        else if (diff === 'intermediate') { chapterData[chapter].supporting_intermediate.total++; if (ans.is_correct) chapterData[chapter].supporting_intermediate.correct++; }
        else if (diff === 'advanced') { chapterData[chapter].supporting_advanced.total++; if (ans.is_correct) chapterData[chapter].supporting_advanced.correct++; }
      }

      if (!chapterData[chapter].concepts[concept]) chapterData[chapter].concepts[concept] = { correct: 0, total: 0 };
      chapterData[chapter].concepts[concept].total++;
      if (ans.is_correct) chapterData[chapter].concepts[concept].correct++;

      if (stage === 'supporting') {
        if (!chapterData[chapter].supportingConcepts[concept]) chapterData[chapter].supportingConcepts[concept] = { basic: false, intermediate: false, advanced: false };
        if (ans.is_correct) {
          if (diff === 'basic') chapterData[chapter].supportingConcepts[concept].basic = true;
          if (diff === 'intermediate') chapterData[chapter].supportingConcepts[concept].intermediate = true;
          if (diff === 'advanced') chapterData[chapter].supportingConcepts[concept].advanced = true;
        }
      }

      ans.tags?.forEach(tag => {
        if (!tag) return;
        if (tag.includes('M-S') || tag.includes('E-S') || tag.includes('S-S')) {
          if (!chapterData[chapter].skillTags[tag]) chapterData[chapter].skillTags[tag] = { correct: 0, total: 0 };
          chapterData[chapter].skillTags[tag].total++;
          if (ans.is_correct) chapterData[chapter].skillTags[tag].correct++;
        }
      });

      if (!ans.is_correct && ans.error_tags && ans.error_tags.length > 0) {
        ans.error_tags.forEach(tag => { if (tag) chapterData[chapter].chosenAnswerTags[tag] = (chapterData[chapter].chosenAnswerTags[tag] || 0) + 1; });
      }
    });

    const chapters = Object.entries(chapterData).map(([chapterName, d]) => {
      const cleanChapterName = chapterName.trim();
      let chapterResults = sessionData?.math_concept_results?.[cleanChapterName];
      
      if (!chapterResults && sessionData?.math_concept_results) {
        const matchingKey = Object.keys(sessionData.math_concept_results).find(key => key.trim().toLowerCase() === cleanChapterName.toLowerCase());
        if (matchingKey) chapterResults = sessionData.math_concept_results[matchingKey];
      }

      const dbChapterTotal = chapterResults?.chapter_total;
      const dbPlacementPoints = chapterResults?.placement_points !== undefined && chapterResults?.placement_points !== null ? chapterResults.placement_points : 0;
      const dbAccuracyPoints = chapterResults?.accuracy_points !== undefined && chapterResults?.accuracy_points !== null ? chapterResults.accuracy_points : 0;
      
      let finalChapterTotal = 0;
      if (dbChapterTotal !== undefined && dbChapterTotal !== null) {
        finalChapterTotal = Math.round(dbChapterTotal);
      } else {
        const cScore = d.core_basic.correct + d.core_intermediate.correct + d.core_advanced.correct;
        const cTotal = d.core_basic.total + d.core_intermediate.total + d.core_advanced.total;
        const bScore = d.backup_basic.correct + d.backup_intermediate.correct;
        const bTotal = d.backup_basic.total + d.backup_intermediate.total;
        const totalQ = cTotal + bTotal + d.supporting_basic.total + d.supporting_intermediate.total + d.supporting_advanced.total;
        const correctQ = cScore + bScore + d.supporting_basic.correct + d.supporting_intermediate.correct + d.supporting_advanced.correct;
        finalChapterTotal = totalQ > 0 ? Math.round((correctQ / totalQ) * 100) : 0;
      }

      const allConcepts = Object.entries(d.concepts).map(([name, stats]) => ({ name, accuracy: stats.total > 0 ? (stats.correct / stats.total) * 100 : 0 })).sort((a, b) => b.accuracy - a.accuracy);
      const totalWrongInChapter = Object.values(d.concepts).reduce((sum, stats) => sum + (stats.total - stats.correct), 0);

      return {
        name: cleanChapterName, chapterTotal: finalChapterTotal, placementPoints: dbPlacementPoints, accuracyPoints: dbAccuracyPoints,
        coreBasic: d.core_basic, coreIntermediate: d.core_intermediate, coreAdvanced: d.core_advanced,
        backupBasic: d.backup_basic, backupIntermediate: d.backup_intermediate,
        supportingBasic: d.supporting_basic, supportingIntermediate: d.supporting_intermediate, supportingAdvanced: d.supporting_advanced,
        skillTags: d.skillTags || {}, concepts: allConcepts, chosenAnswerTags: d.chosenAnswerTags || {}, 
        totalWrongInChapter: totalWrongInChapter, totalQuestions: 0, totalCorrect: 0
      };
    });

    const overallScore = chapters.length > 0 ? Math.round(chapters.reduce((sum, ch) => sum + ch.chapterTotal, 0) / chapters.length) : 0;
    let overallBand = 'Foundational';
    if (overallScore >= 86) overallBand = 'Advanced';
    else if (overallScore >= 71) overallBand = 'Proficient';
    else if (overallScore >= 51) overallBand = 'Developing';
    else if (overallScore >= 31) overallBand = 'Emerging';

    const sessionErrorTallies = sessionData?.error_tag_tallies || {};
    const totalErrorTagsTriggered = sessionErrorTallies._total_triggered || 0;
    const overallChosenAnswerTagBreakdown = Object.entries(sessionErrorTallies).filter(([tag]) => tag !== '_total_triggered').map(([tag, count]) => ({ tag, count, percentage: totalErrorTagsTriggered > 0 ? ((count / totalErrorTagsTriggered) * 100).toFixed(1) : 0 })).sort((a, b) => b.count - a.count);

    // ✅ PURE DATABASE READ: The backend script has permanently fixed the DB.
    // We no longer calculate on the fly. Both Admin and Parent will read this exact same correct data.
    const overallSkillTags = (sessionData?.skill_tag_tallies && typeof sessionData.skill_tag_tallies === 'object') 
      ? sessionData.skill_tag_tallies 
      : {};

    return { overallScore, overallBand, chapters, overallSkillTags, totalWrong: answers.filter(a => !a.is_correct).length, overallChosenAnswerTagBreakdown };
  }, [data, isMath]);

  const englishOverallData = useMemo(() => {
    if (!isEnglish) return null;
    const score = data?.assignment?.score !== null && data?.assignment?.score !== undefined ? Math.round(data.assignment.score) : null;
    if (score === null) return null;
    return { score, competencyWord: getCompetencyWord(score), competencyColor: getCompetencyColor(score), totalCorrect: data.answers?.filter(a => a.is_correct).length || 0, totalQuestions: data.answers?.length || 0 };
  }, [data, isEnglish]);

  const englishBandData = useMemo(() => {
    if (!isEnglish || !data?.answers || data.answers.length === 0) return [];
    const bandStats = {};
    data.answers.forEach(ans => {
      const band = ans.metadata?.band || 'Unknown';
      if (!bandStats[band]) bandStats[band] = { original: { correct: 0, total: 0 }, backup: { correct: 0, total: 0 }, tags: {} };
      const target = ans.stage === 'backup' ? bandStats[band].backup : bandStats[band].original;
      target.total += 1; if (ans.is_correct) target.correct += 1;
      const tags = ans.tags || [];
      if (tags.length > 0) {
        const combo = tags.sort().join(' + ');
        if (!bandStats[band].tags[combo]) bandStats[band].tags[combo] = { correct: 0, total: 0 };
        bandStats[band].tags[combo].total += 1; if (ans.is_correct) bandStats[band].tags[combo].correct += 1;
      }
    });
    return Object.entries(bandStats).map(([band, stats]) => {
      const totalCorrect = stats.original.correct + stats.backup.correct;
      const totalQuestions = stats.original.total + stats.backup.total;
      const percentage = totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0;
      return { band, originalScore: `${stats.original.correct}/${stats.original.total}`, backupScore: `${stats.backup.correct}/${stats.backup.total}`, status: percentage >= 60 ? 'Pass' : 'Fail', percentage: percentage.toFixed(1), tags: Object.entries(stats.tags).map(([combo, tagStats]) => ({ combo, correct: tagStats.correct, total: tagStats.total, accuracy: tagStats.total > 0 ? ((tagStats.correct / tagStats.total) * 100).toFixed(1) : 0 })) };
    }).sort((a, b) => { const order = { 'A1': 1, 'A2': 2, 'B1': 3, 'B2': 4, 'C1': 5, 'C2': 6 }; return (order[a.band] || 99) - (order[b.band] || 99); });
  }, [data, isEnglish]);

  const englishTagStats = useMemo(() => {
    if (!isEnglish || !data?.answers || data.answers.length === 0) return [];
    const stats = {};
    data.answers.forEach(ans => { ans.tags?.forEach(tag => { if (!tag) return; if (!stats[tag]) stats[tag] = { correct: 0, total: 0 }; stats[tag].total++; if (ans.is_correct) stats[tag].correct++; }); });
    return Object.entries(stats).map(([tag, d]) => ({ tag, correct: d.correct, total: d.total, percentage: d.total > 0 ? Math.round((d.correct / d.total) * 100) : 0 })).sort((a, b) => a.percentage - b.percentage);
  }, [data, isEnglish]);

  const simpleStats = useMemo(() => {
    if (!data || isMath || isEnglish || !data.answers || data.answers.length === 0) return [];
    const stats = {};
    data.answers.forEach(ans => {
      const category = isScience ? (ans.metadata?.concept || 'Unknown Concept') : (ans.metadata?.band || 'Unknown');
      if (!stats[category]) stats[category] = { correct: 0, total: 0 };
      stats[category].total += 1; if (ans.is_correct) stats[category].correct += 1;
    });
    return Object.entries(stats).map(([category, d]) => ({ category, correct: d.correct, total: d.total, percentage: d.total > 0 ? Math.round((d.correct / d.total) * 100) : 0 })).sort((a, b) => b.percentage - a.percentage);
  }, [data, isMath, isEnglish, isScience]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>⏳ Loading attempt details...</div>;
  if (error) return <div style={{ maxWidth: 1000, margin: '40px auto', padding: 24 }}><div style={{ padding: 20, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 20 }}>❌ <strong>Error:</strong> {error}</div><button onClick={() => navigate(-1)} style={{ padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{t('back')}</button></div>;
  if (!data || !data.assignment) return <div style={{ maxWidth: 1000, margin: '40px auto', padding: 24 }}><div style={{ padding: 20, background: '#fff3cd', color: '#856404', borderRadius: 6 }}>️ No data available for this attempt.</div><button onClick={() => navigate(-1)} style={{ marginTop: 20, padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{t('back')}</button></div>;

  const { assignment, answers = [], sessionData } = data;

  const getScoreColor = (pct) => pct >= 80 ? '#28a745' : pct >= 60 ? '#ffc107' : '#dc3545';
  const getBandColor = (band) => ({ 'Foundational': '#6c757d', 'Emerging': '#dc3545', 'Developing': '#ffc107', 'Proficient': '#007BFF', 'Advanced': '#28a745' }[band] || '#666');
  const getConceptColor = (acc) => acc >= 80 ? '#28a745' : acc >= 50 ? '#ffc107' : '#dc3545';

  const getDuration = () => {
    if (assignment.duration_seconds) return `${Math.floor(assignment.duration_seconds / 60)}m ${assignment.duration_seconds % 60}s`;
    if (assignment.started_at && assignment.completed_at) {
      const diff = Math.floor((new Date(assignment.completed_at).getTime() - new Date(assignment.started_at).getTime()) / 1000);
      if (diff >= 0) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
    }
    return 'N/A';
  };

  const getExpectedGrade = () => {
    if (!assignment.current_grade || !assignment.completed_at) return null;
    const examGrade = parseInt(assignment.current_grade);
    if (isNaN(examGrade)) return null;
    return examGrade + (new Date().getFullYear() - new Date(assignment.completed_at).getFullYear());
  };

  return (
    <div style={{ maxWidth: 1200, margin: '20px auto', padding: '15px', background: '#f8f9fa', minHeight: '100vh', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 15 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem' }}>{assignment.exam_name}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
          <div style={{ display: 'flex', border: '1px solid #ced4da', borderRadius: 6, overflow: 'hidden' }}>
            <button onClick={() => setLang('en')} style={{ background: lang === 'en' ? '#007BFF' : '#fff', color: lang === 'en' ? '#fff' : '#333', border: 'none', padding: '8px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>EN</button>
            <button onClick={() => setLang('cn')} style={{ background: lang === 'cn' ? '#007BFF' : '#fff', color: lang === 'cn' ? '#fff' : '#333', border: 'none', borderLeft: '1px solid #ced4da', padding: '8px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>CN</button>
          </div>
          <button onClick={() => navigate(-1)} style={{ padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{t('back')}</button>
        </div>
      </div>

      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 15, borderBottom: '1px solid #eee', paddingBottom: 15, marginBottom: 15 }}>
          <div>
            <strong>{t('student')}:</strong> {assignment.first_name || ''} {assignment.last_name || ''}
            <span style={{ marginLeft: 10, color: '#666', display: 'block', marginTop: 5 }}>
              {assignment.current_grade ? (
                <>({t('grade')} {assignment.current_grade}
                  {getExpectedGrade() && getExpectedGrade() !== parseInt(assignment.current_grade) && (
                    <span style={{ color: '#28a745', fontWeight: 600 }}> → Expected: {t('grade')} {getExpectedGrade()}</span>
                  )})
                </>
              ) : (<span style={{ color: '#dc3545', fontWeight: 500 }}>({t('grade')}: N/A)</span>)}
            </span>
          </div>
          <div><strong>{t('school')}:</strong> {assignment.school_name || 'N/A'}</div>
          <div><strong>{t('centerId')}:</strong> {assignment.center_id || 'N/A'}</div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 15, borderBottom: '1px solid #eee', paddingBottom: 15, marginBottom: 15 }}>
          <div><strong>{t('subject')}:</strong> <span style={{ textTransform: 'capitalize' }}>{assignment.exam_topic || 'N/A'}</span></div>
          <div><strong>{t('status')}:</strong> <span style={{ textTransform: 'capitalize' }}>{assignment.status || 'N/A'}</span></div>
          <div>
            <strong>{t('totalQuestions')}:</strong> {answers.length}
            {isMath && answers.length !== uniqueQuestionCount && (<span style={{ fontSize: 12, color: '#666', marginLeft: 5 }}>({uniqueQuestionCount} {t('unique')})</span>)}
          </div>
          <div><strong>{t('duration')}:</strong> {getDuration()}</div>
        </div>
        <div><strong>{t('completed')}:</strong> {assignment.completed_at ? formatDate(assignment.completed_at) : t('inProgress')}</div>
      </div>

      {isMath && mathData ? (
        <>
          {!isParent && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              <button onClick={() => setActiveTab('overview')} style={{ padding: '10px 20px', background: activeTab === 'overview' ? '#fff' : '#e9ecef', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontWeight: activeTab === 'overview' ? 600 : 400 }}>👁 Overview</button>
              <button onClick={() => setActiveTab('detailed')} style={{ padding: '10px 20px', background: activeTab === 'detailed' ? '#fff' : '#e9ecef', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontWeight: activeTab === 'detailed' ? 600 : 400 }}>🔍 Detailed Analysis</button>
            </div>
          )}

          {(activeTab === 'overview' || isParent) && (
            <>
              <div style={{ background: '#fff', padding: 30, borderRadius: 8, border: '2px solid #007BFF', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginBottom: 10, flexWrap: 'wrap' }}>
                  {isParent ? (
                    <>
                      <span style={{ padding: '10px 24px', background: getCompetencyColor(mathData.overallScore), color: '#fff', borderRadius: 12, fontSize: 24, fontWeight: 700 }}>
                        {t(getCompetencyWord(mathData.overallScore).toLowerCase())}
                      </span>
                      <span style={{ fontSize: 16, color: '#666' }}>{t('overallMath')}</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 56, fontWeight: 'bold', color: '#333' }}>{mathData.overallScore}</span>
                      <span style={{ fontSize: 24, color: '#666' }}>/ 100</span>
                      <span style={{ padding: '6px 16px', background: getBandColor(mathData.overallBand), color: '#fff', borderRadius: 12, fontSize: 14, fontWeight: 600 }}>{t(mathData.overallBand.toLowerCase())}</span>
                      <span style={{ fontSize: 16, color: '#666' }}>{t('overallMath')}</span>
                    </>
                  )}
                </div>
                
                {!isParent ? (
                  <>
                    <div style={{ position: 'relative', height: 30, width: '100%', padding: 0, margin: 0, background: 'linear-gradient(to right, #e2e3e5 0%, #e2e3e5 20%, #f8d7da 20%, #f8d7da 40%, #fff3cd 40%, #fff3cd 60%, #cce5ff 60%, #cce5ff 80%, #d4edda 80%, #d4edda 100%)', borderRadius: 4, marginBottom: 10 }}>
                      <div style={{ position: 'absolute', left: `${mathData.overallScore}%`, top: -5, width: 4, height: 40, background: '#333', transform: 'translateX(-50%)', zIndex: 10 }}></div>
                    </div>
                    <div style={{ position: 'relative', height: 15, width: '100%', marginTop: 5 }}>
                      <span style={{ position: 'absolute', left: '0%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>0</span>
                      <span style={{ position: 'absolute', left: '20%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>20</span>
                      <span style={{ position: 'absolute', left: '40%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>40</span>
                      <span style={{ position: 'absolute', left: '60%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>60</span>
                      <span style={{ position: 'absolute', left: '80%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>80</span>
                      <span style={{ position: 'absolute', left: '100%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>100</span>
                    </div>
                  </>
                ) : (
                  <div style={{ padding: '30px 20px', background: '#f8f9fa', borderRadius: 8, marginTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                      <div style={{ position: 'absolute', top: '20px', left: '40px', right: '40px', height: '2px', background: '#e0e0e0' }}></div>
                      {COMPETENCY_LEVELS.map((level) => {
                        const isActive = mathData.overallScore >= level.minScore && mathData.overallScore <= level.maxScore;
                        return (
                          <div key={level.name} style={{ textAlign: 'center', zIndex: 1 }}>
                            {isActive ? (
                              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'transparent', margin: '0 auto 10px', border: `4px solid ${level.color}`, position: 'relative' }}>
                                <div style={{ width: 16, height: 16, borderRadius: '50%', background: level.color, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}></div>
                              </div>
                            ) : (
                              <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'transparent', margin: '0 auto 10px', border: `2px solid ${level.color}` }}></div>
                            )}
                            <div style={{ fontSize: 13, fontWeight: isActive ? 700 : 400, color: isActive ? level.color : '#ccc' }}>
                              {t(level.name)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {mathData.overallChosenAnswerTagBreakdown && mathData.overallChosenAnswerTagBreakdown.length > 0 && (
                <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
                  <h3 style={{ marginTop: 0, fontSize: 16, marginBottom: 15 }}>{t('misconceptions')}</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ padding: 10, textAlign: 'left' }}>{t('answerTag')}</th>
                          <th style={{ padding: 10, textAlign: 'center' }}>{t('percentErrors')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mathData.overallChosenAnswerTagBreakdown.map((item, idx) => (
                          <tr key={item.tag} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: 10 }}><span style={{ fontWeight: 600 }}>{getTagMeaning(item.tag, 'error')}</span></td>
                            <td style={{ padding: 10, textAlign: 'center', fontWeight: 'bold', color: idx === 0 ? '#dc3545' : '#666' }}>{item.percentage}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
                <h3 style={{ marginTop: 0, fontSize: 16, marginBottom: 15 }}>{t('questionTagAnalysis')}</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                        <th style={{ padding: 10, textAlign: 'left' }}>{t('tag')}</th>
                        <th style={{ padding: 10, textAlign: 'center' }}>{t('accuracy')}</th>
                        <th style={{ padding: 10, textAlign: 'center' }}>{t('performance')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(mathData.overallSkillTags || {}).sort((a, b) => {
                        const accA = a[1].total > 0 ? (a[1].correct / a[1].total) : 0;
                        const accB = b[1].total > 0 ? (b[1].correct / b[1].total) : 0;
                        return accA - accB;
                      }).map(([tag, stats]) => {
                        const accuracy = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;
                        const color = accuracy >= 80 ? '#28a745' : accuracy >= 60 ? '#ffc107' : '#dc3545';
                        return (
                          <tr key={tag} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: 10 }}><span style={{ fontWeight: 600 }}>{getTagMeaning(tag, 'question')}</span></td>
                            <td style={{ padding: 10, textAlign: 'center', fontWeight: 'bold', color }}>{accuracy.toFixed(0)}%</td>
                            <td style={{ padding: 10 }}>
                              <div style={{ width: '100%', height: 8, background: '#e9ecef', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ width: `${accuracy}%`, height: '100%', background: color }}></div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <h3 style={{ marginBottom: 15 }}>{t('chaptersGlance')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 15 }}>
                {mathData.chapters.map((chapter, idx) => (
                  <div key={idx} style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
                    <h4 style={{ marginTop: 0, marginBottom: 15 }}>{chapter.name}</h4>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 15, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 36, fontWeight: 'bold', color: '#333' }}>{chapter.chapterTotal}%</span>
                    </div>
                    <div style={{ position: 'relative', height: 24, width: '100%', padding: 0, margin: 0, background: '#e9ecef', borderRadius: 4, marginBottom: 10 }}>
                      <div style={{ position: 'absolute', left: `${chapter.chapterTotal}%`, top: -4, width: 4, height: 32, background: '#333', transform: 'translateX(-50%)', borderRadius: 2, zIndex: 10 }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {!isParent && activeTab === 'detailed' && (
            <>
              <button onClick={() => setActiveTab('overview')} style={{ marginBottom: 20, padding: '8px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{t('backToOverview')}</button>
              <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15, flexWrap: 'wrap', gap: 10 }}>
                  <h3 style={{ margin: 0 }}>{t('competencyContribution')}</h3>
                  <span style={{ padding: '6px 16px', background: getBandColor(mathData.overallBand), color: '#fff', borderRadius: 12, fontSize: 13, fontWeight: 600 }}>{t('overallMath')} {mathData.overallScore} · {t(mathData.overallBand.toLowerCase())}</span>
                </div>
                <div style={{ display: 'flex', gap: 20, marginBottom: 15, fontSize: 12, color: '#666', flexWrap: 'wrap' }}><span> {t('placement')}</span><span> {t('accuracyScore')}</span></div>
                {mathData.chapters.map((chapter, idx) => (
                  <div key={idx} style={{ marginBottom: 15 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, flexWrap: 'wrap', gap: 5 }}>
                      <span style={{ fontWeight: 600 }}>{chapter.name}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ padding: '3px 10px', background: getBandColor(getCompetencyWord(chapter.chapterTotal).toLowerCase()), color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{t(getCompetencyWord(chapter.chapterTotal).toLowerCase())}</span>
                        <span style={{ fontWeight: 'bold', fontSize: 16 }}>{Math.round(chapter.chapterTotal)}%</span>
                      </div>
                    </div>
                    <div style={{ height: 20, background: '#e9ecef', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
                      <div style={{ width: `${(chapter.placementPoints / 60) * 100}%`, background: '#007BFF', height: '100%' }}></div>
                      <div style={{ width: `${(chapter.accuracyPoints / 40) * 100}%`, background: '#28a745', height: '100%' }}></div>
                    </div>
                  </div>
                ))}
              </div>
              
              {mathData.chapters.map((chapter, idx) => (
                <div key={idx} style={{ background: '#fff', padding: 25, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 15, borderBottom: '2px solid #f8f9fa', flexWrap: 'wrap', gap: 10 }}>
                    <h3 style={{ margin: 0 }}>Chapter {idx + 1} — {chapter.name}</h3>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ padding: '4px 12px', background: chapter.chapterTotal >= 80 ? '#d4edda' : chapter.chapterTotal >= 50 ? '#fff3cd' : '#f8d7da', color: chapter.chapterTotal >= 80 ? '#155724' : chapter.chapterTotal >= 50 ? '#856404' : '#721c24', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{t('core')}: {chapter.chapterTotal >= 80 ? t('pass') : chapter.chapterTotal >= 50 ? 'Within' : t('fail')}</span>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: 20, paddingTop: 15, borderTop: '1px solid #eee' }}>
                    <h4 style={{ fontSize: 14, color: '#333', fontWeight: 600, marginBottom: 10 }}>{t('howResultReached')}</h4>
                    <div style={{ fontSize: 14, lineHeight: 2.2, background: '#f8f9fa', padding: 15, borderRadius: 6 }}>
                      <div style={{ fontWeight: 600, color: '#007BFF', marginBottom: 5, marginTop: 5 }}>Core Phase</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 10 }}>
                        <span>Core - Basic</span>
                        <strong>{chapter.coreBasic.correct} / {chapter.coreBasic.total} ({chapter.coreBasic.total > 0 ? Math.round((chapter.coreBasic.correct / chapter.coreBasic.total) * 100) : 0}%)</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 10 }}>
                        <span>Core - Intermediate</span>
                        <strong>{chapter.coreIntermediate.correct} / {chapter.coreIntermediate.total} ({chapter.coreIntermediate.total > 0 ? Math.round((chapter.coreIntermediate.correct / chapter.coreIntermediate.total) * 100) : 0}%)</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 10 }}>
                        <span>Core - Advanced</span>
                        <strong>{chapter.coreAdvanced.correct} / {chapter.coreAdvanced.total} ({chapter.coreAdvanced.total > 0 ? Math.round((chapter.coreAdvanced.correct / chapter.coreAdvanced.total) * 100) : 0}%)</strong>
                      </div>

                      {(chapter.backupBasic.total > 0 || chapter.backupIntermediate.total > 0) && (
                        <>
                          <div style={{ fontWeight: 600, color: '#dc3545', marginBottom: 5, marginTop: 15 }}>Backup Phase</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 10 }}>
                            <span>Backup - Basic</span>
                            <strong>{chapter.backupBasic.correct} / {chapter.backupBasic.total} ({chapter.backupBasic.total > 0 ? Math.round((chapter.backupBasic.correct / chapter.backupBasic.total) * 100) : 0}%)</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 10 }}>
                            <span>Backup - Intermediate</span>
                            <strong>{chapter.backupIntermediate.correct} / {chapter.backupIntermediate.total} ({chapter.backupIntermediate.total > 0 ? Math.round((chapter.backupIntermediate.correct / chapter.backupIntermediate.total) * 100) : 0}%)</strong>
                          </div>
                        </>
                      )}

                      <div style={{ fontWeight: 600, color: '#28a745', marginBottom: 5, marginTop: 15 }}>Supporting Phase</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 10 }}>
                        <span>Supporting - Basic</span>
                        <strong>{chapter.supportingBasic.correct} / {chapter.supportingBasic.total} ({chapter.supportingBasic.total > 0 ? Math.round((chapter.supportingBasic.correct / chapter.supportingBasic.total) * 100) : 0}%)</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 10 }}>
                        <span>Supporting - Intermediate</span>
                        <strong>{chapter.supportingIntermediate.correct} / {chapter.supportingIntermediate.total} ({chapter.supportingIntermediate.total > 0 ? Math.round((chapter.supportingIntermediate.correct / chapter.supportingIntermediate.total) * 100) : 0}%)</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 10 }}>
                        <span>Supporting - Advanced</span>
                        <strong>{chapter.supportingAdvanced.correct} / {chapter.supportingAdvanced.total} ({chapter.supportingAdvanced.total > 0 ? Math.round((chapter.supportingAdvanced.correct / chapter.supportingAdvanced.total) * 100) : 0}%)</strong>
                      </div>

                      <div style={{ marginTop: 15, paddingTop: 10, borderTop: '1px dashed #ccc', fontWeight: 600 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Placement Points:</span><strong>{Math.round(chapter.placementPoints)} / 60</strong></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Accuracy Points:</span><strong>{Math.round(chapter.accuracyPoints)} / 40</strong></div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 30, marginBottom: 20 }}>
                    <div>
                      <h4 style={{ marginTop: 0, fontSize: 13, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>{t('accuracyByConcept')}</h4>
                      {chapter.concepts.map((concept, cIdx) => (
                        <div key={cIdx} style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                          <span style={{ minWidth: 20, color: '#666' }}>{cIdx + 1}</span>
                          <span style={{ flex: 1, fontSize: 14 }}>{concept.name}</span>
                          <div style={{ width: 120, height: 12, background: '#e9ecef', borderRadius: 6, overflow: 'hidden', margin: '0 10px' }}>
                            <div style={{ width: `${concept.accuracy}%`, height: '100%', background: getConceptColor(concept.accuracy) }}></div>
                          </div>
                          <span style={{ fontWeight: 'bold', minWidth: 40, textAlign: 'right' }}>{Math.round(concept.accuracy)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: 20, paddingTop: 15, borderTop: '1px solid #eee' }}>
                    <h4 style={{ fontSize: 13, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{t('answerTagMisconception')}</h4>
                    {chapter.chosenAnswerTags && Object.keys(chapter.chosenAnswerTags).length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                              <th style={{ padding: 8, textAlign: 'left' }}>{t('answerTag')}</th>
                              <th style={{ padding: 8, textAlign: 'center' }}>{t('count')}</th>
                              <th style={{ padding: 8, textAlign: 'center' }}>{t('percentChapterErrors')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(chapter.chosenAnswerTags).sort((a, b) => b[1] - a[1]).map(([tag, count]) => {
                              const totalTagsTriggered = Object.values(chapter.chosenAnswerTags).reduce((sum, val) => sum + val, 0);
                              const pct = totalTagsTriggered > 0 ? ((count / totalTagsTriggered) * 100).toFixed(1) : 0;
                              return (
                                <tr key={tag} style={{ borderBottom: '1px solid #eee' }}>
                                  <td style={{ padding: 8 }}><span style={{ fontWeight: 600 }}>{getTagMeaning(tag, 'error')}</span></td>
                                  <td style={{ padding: 8, textAlign: 'center', fontWeight: 600 }}>{count}</td>
                                  <td style={{ padding: 8, textAlign: 'center', color: '#dc3545', fontWeight: 600 }}>{pct}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : <p style={{ fontSize: 13, color: '#999', fontStyle: 'italic', margin: 0 }}>{t('noMisconceptions')}</p>}
                  </div>
                </div>
              ))}
              
              <div style={{ background: '#fff', padding: 25, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
                <h3 style={{ marginTop: 0, color: '#343a40', marginBottom: 20 }}>{t('questionByQuestion')}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                  {answers.map((ans, idx) => (
                    <div key={ans.id || idx} style={{ border: `2px solid ${ans.is_correct ? '#28a745' : '#dc3545'}`, borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                      <div style={{ padding: 15, background: ans.is_correct ? '#d4edda' : '#f8d7da', borderBottom: '1px solid #ddd' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5, flexWrap: 'wrap' }}>
                              <strong style={{ fontSize: 16 }}>Question {idx + 1}</strong>
                              <span style={{ padding: '4px 12px', background: ans.is_correct ? '#28a745' : '#dc3545', color: '#fff', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{ans.is_correct ? '✓ Correct' : `✗ ${t('incorrect')}`}</span>
                            </div>
                            {ans.tags && ans.tags.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
                                {ans.tags.map((tag, tIdx) => {
                                  let bgColor = '#fff3cd', textColor = '#856404';
                                  if (tag.includes('M-S') || tag.includes('E-S') || tag.includes('S-S')) { bgColor = '#e7f1ff'; textColor = '#0056b3'; }
                                  if (tag.includes('M-E') || tag.includes('E-E') || tag.includes('S-E')) { bgColor = '#f8d7da'; textColor = '#721c24'; }
                                  return <span key={tIdx} style={{ padding: '2px 8px', background: bgColor, color: textColor, borderRadius: 4, fontSize: 11, fontFamily: 'monospace' }}>{getTagMeaning(tag, 'question')}</span>;
                                })}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', fontSize: 12, color: '#666' }}>
                            <div>{ans.metadata?.chapter} - {ans.metadata?.concept}</div>
                            <div style={{ textTransform: 'capitalize' }}>{ans.metadata?.difficulty} | {ans.metadata?.concept_type}</div>
                          </div>
                        </div>
                      </div>
                      <div style={{ padding: 15 }}>
                        <div style={{ fontSize: 15, marginBottom: 15, lineHeight: 1.6, color: '#333' }}><MathText>{ans.question_text}</MathText></div>
                        {ans.image_url && <div style={{ marginBottom: 15, textAlign: 'center' }}><img src={getImageUrl(ans.image_url)} alt="Question" style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 6, border: '1px solid #ddd' }} onError={(e) => { e.target.style.display = 'none'; }} /></div>}
                        <div style={{ display: 'grid', gap: 10 }}>
                          {['A', 'B', 'C', 'D'].map(opt => {
                            if (!ans.options?.[opt]) return null;
                            const isCorrect = ans.correct_answer === opt;
                            const isSelected = ans.selected_answer === opt;
                            return (
                              <div key={opt} style={{ padding: 12, background: isCorrect ? '#d4edda' : isSelected ? '#f8d7da' : '#f8f9fa', border: `2px solid ${isCorrect ? '#28a745' : isSelected ? '#dc3545' : '#ddd'}`, borderRadius: 6, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                <strong style={{ minWidth: 25 }}>{opt}.</strong>
                                <span style={{ flex: 1 }}><MathText>{ans.options[opt]}</MathText></span>
                                {isCorrect && <span style={{ color: '#28a745', fontWeight: 'bold', fontSize: 18 }}>✓</span>}
                                {isSelected && !isCorrect && <span style={{ color: '#dc3545', fontWeight: 'bold', fontSize: 18 }}>✗</span>}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ marginTop: 15, padding: 10, background: '#f8f9fa', borderRadius: 6, fontSize: 13 }}>
                          <strong>{t('correctAnswer')}:</strong> <span style={{ color: '#28a745', fontWeight: 'bold' }}>{ans.correct_answer}</span> | 
                          <strong style={{ marginLeft: 15 }}>{t('studentSelected')}:</strong> <span style={{ color: ans.is_correct ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>{ans.selected_answer || t('notAnswered')}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#666', borderTop: '1px solid #eee', paddingTop: 10, marginTop: 10 }}>
                          {isMath && (
                            <> 
                              <strong>{t('difficulty')}:</strong> {ans.metadata?.difficulty} | <strong>{t('type')}:</strong> {ans.metadata?.concept_type} 
                              {ans.tags?.length > 0 && <div style={{marginTop: 5}}><strong>{t('tags')}:</strong> {ans.tags.map(t => getTagMeaning(t, 'question')).join(', ')}</div>} 
                              {ans.error_tags?.length > 0 && <div style={{marginTop: 5, color: '#dc3545'}}><strong>{t('misconceptionsSmall')}:</strong> {ans.error_tags.map(t => getTagMeaning(t, 'error')).join(', ')}</div>} 
                            </>
                          )}
                          {isScience && (
                            <> 
                              <strong>{t('subConcept')}:</strong> {ans.metadata?.sub_concept} | <strong>{t('type')}:</strong> {ans.metadata?.concept_type} 
                              {ans.skillset_tags?.length > 0 && <div style={{marginTop: 5}}><strong>{t('skillsets')}:</strong> {ans.skillset_tags.join(', ')}</div>} 
                              {ans.error_tags?.length > 0 && !ans.is_correct && <div style={{marginTop: 5, color: '#dc3545'}}><strong>{t('misconceptionsSmall')}:</strong> {ans.error_tags.map(t => `${t} (${getTagMeaning(t, 'error')})`).join(', ')}</div>} 
                            </>
                          )}
                          {isEnglish && (
                            <>
                              <strong>{t('band')}:</strong> {ans.metadata?.band}
                              {ans.tags?.length > 0 && <div style={{marginTop: 5}}><strong>{t('tags')}:</strong> {ans.tags.map(t => `${t} - ${getTagMeaning(t, 'question')}`).join(', ')}</div>}
                              {ans.error_tags?.length > 0 && !ans.is_correct && <div style={{marginTop: 5, color: '#dc3545'}}><strong>{t('misconceptionsSmall')}:</strong> {ans.error_tags.map(t => `${t} - ${getTagMeaning(t, 'error')}`).join(', ')}</div>}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      ) : isEnglish ? (
        <>
          {!isParent && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              <button onClick={() => setActiveTab('overview')} style={{ padding: '10px 20px', background: activeTab === 'overview' ? '#fff' : '#e9ecef', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontWeight: activeTab === 'overview' ? 600 : 400 }}>👁 Overview</button>
              <button onClick={() => setActiveTab('detailed')} style={{ padding: '10px 20px', background: activeTab === 'detailed' ? '#fff' : '#e9ecef', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontWeight: activeTab === 'detailed' ? 600 : 400 }}>🔍 Detailed Analysis</button>
            </div>
          )}

          {activeTab === 'overview' && (
            englishOverallData ? (
              <>
                <div style={{ background: '#fff', padding: 30, borderRadius: 8, border: '2px solid #007BFF', marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginBottom: 10, flexWrap: 'wrap' }}>
                    {isParent ? (
                      <>
                        <span style={{ padding: '10px 24px', background: englishOverallData.competencyColor, color: '#fff', borderRadius: 12, fontSize: 24, fontWeight: 700 }}>
                          {t(englishOverallData.competencyWord.toLowerCase())}
                        </span>
                        <span style={{ fontSize: 16, color: '#666' }}>{t('overallEnglish')}</span>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: 56, fontWeight: 'bold', color: '#333' }}>{englishOverallData.score}</span>
                        <span style={{ fontSize: 24, color: '#666' }}>/ 100</span>
                        <span style={{ padding: '6px 16px', background: englishOverallData.competencyColor, color: '#fff', borderRadius: 12, fontSize: 14, fontWeight: 600 }}>{t(englishOverallData.competencyWord.toLowerCase())}</span>
                        <span style={{ fontSize: 16, color: '#666' }}>{t('overallEnglish')}</span>
                      </>
                    )}
                  </div>
                  <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>{t('overallAccuracy', { correct: englishOverallData.totalCorrect, total: englishOverallData.totalQuestions })}</p>
                  
                  {!isParent ? (
                    <>
                      <div style={{ position: 'relative', height: 30, width: '100%', padding: 0, margin: 0, background: 'linear-gradient(to right, #e2e3e5 0%, #e2e3e5 20%, #f8d7da 20%, #f8d7da 40%, #fff3cd 40%, #fff3cd 60%, #cce5ff 60%, #cce5ff 80%, #d4edda 80%, #d4edda 100%)', borderRadius: 4, marginBottom: 10 }}>
                        <div style={{ position: 'absolute', left: `${englishOverallData.score}%`, top: -5, width: 4, height: 40, background: '#333', transform: 'translateX(-50%)', zIndex: 10 }}></div>
                      </div>
                      <div style={{ position: 'relative', height: 15, width: '100%', marginTop: 5 }}>
                        <span style={{ position: 'absolute', left: '0%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>0</span>
                        <span style={{ position: 'absolute', left: '20%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>20</span>
                        <span style={{ position: 'absolute', left: '40%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>40</span>
                        <span style={{ position: 'absolute', left: '60%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>60</span>
                        <span style={{ position: 'absolute', left: '80%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>80</span>
                        <span style={{ position: 'absolute', left: '100%', transform: 'translateX(-50%)', fontSize: 10, color: '#999' }}>100</span>
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: '30px 20px', background: '#f8f9fa', borderRadius: 8, marginTop: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                        <div style={{ position: 'absolute', top: '20px', left: '40px', right: '40px', height: '2px', background: '#e0e0e0' }}></div>
                        {COMPETENCY_LEVELS.map((level) => {
                          const isActive = englishOverallData.score >= level.minScore && englishOverallData.score <= level.maxScore;
                          return (
                            <div key={level.name} style={{ textAlign: 'center', zIndex: 1 }}>
                              {isActive ? (
                                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'transparent', margin: '0 auto 10px', border: `4px solid ${level.color}`, position: 'relative' }}>
                                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: level.color, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}></div>
                                </div>
                              ) : (
                                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'transparent', margin: '0 auto 10px', border: `2px solid ${level.color}` }}></div>
                              )}
                              <div style={{ fontSize: 13, fontWeight: isActive ? 700 : 400, color: isActive ? level.color : '#ccc' }}>
                                {t(level.name)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
                  <h3 style={{ marginTop: 0, fontSize: 16, marginBottom: 15 }}>{t('questionTagAnalysis')}</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ padding: 12, textAlign: 'left' }}>{t('tag')}</th>
                          <th style={{ padding: 12, textAlign: 'center', width: '120px' }}>{t('accuracy')}</th>
                          <th style={{ padding: 12, textAlign: 'center', width: '150px' }}>{t('performance')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {englishTagStats.map((stat) => {
                          const color = getScoreColor(stat.percentage);
                          const meaning = getTagMeaning(stat.tag, 'question');
                          const displayText = meaning !== stat.tag ? meaning : stat.tag;
                          return (
                            <tr key={stat.tag} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: 12, fontWeight: 500 }}>{displayText}</td>
                              <td style={{ padding: 12, textAlign: 'center', fontWeight: 'bold', color }}>{stat.percentage}%</td>
                              <td style={{ padding: 12 }}>
                                <div style={{ width: '100%', height: 10, background: '#e9ecef', borderRadius: 5, overflow: 'hidden' }}>
                                  <div style={{ width: `${stat.percentage}%`, height: '100%', background: color, transition: 'width 0.3s ease' }}></div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: '#666', background: '#fff', borderRadius: 8, border: '1px solid #ddd' }}>
                <h3>Score data is not available</h3>
                <p>The exam score has not been calculated or saved yet.</p>
                <p>Please ensure the student has fully completed the exam, or check the browser console (F12) for API errors.</p>
              </div>
            )
          )}

          {!isParent && activeTab === 'detailed' && (
            <>
              <button onClick={() => setActiveTab('overview')} style={{ marginBottom: 20, padding: '8px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{t('backToOverview')}</button>
              <h3 style={{ marginBottom: 15 }}>{t('performanceByBand')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 15, marginBottom: 30 }}>
                {englishBandData.map((bandData, idx) => (
                  <div key={idx} style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15, paddingBottom: 10, borderBottom: '1px solid #eee' }}>
                      <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#007BFF' }}>{t('band')} {bandData.band}</h4>
                      <span style={{ padding: '4px 12px', background: bandData.status === 'Pass' ? '#d4edda' : '#f8d7da', color: bandData.status === 'Pass' ? '#155724' : '#721c24', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                        {bandData.status === 'Pass' ? t('pass') : t('fail')}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 15 }}>
                      <div style={{ background: '#f8f9fa', padding: 10, borderRadius: 6, textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>{t('originalScore')}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#333' }}>{bandData.originalScore}</div>
                      </div>
                      <div style={{ background: '#f8f9fa', padding: 10, borderRadius: 6, textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>{t('backupScore')}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#333' }}>{bandData.backupScore}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 5 }}>{t('totalBandAccuracy')}</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: getScoreColor(parseFloat(bandData.percentage)) }}>{bandData.percentage}%</div>
                      <div style={{ width: '100%', height: 8, background: '#e9ecef', borderRadius: 4, overflow: 'hidden', marginTop: 8 }}>
                        <div style={{ width: `${bandData.percentage}%`, height: '100%', background: getScoreColor(parseFloat(bandData.percentage)) }}></div>
                      </div>
                    </div>
                    {bandData.tags.length > 0 && (
                      <div style={{ marginTop: 20, paddingTop: 15, borderTop: '1px dashed #ddd' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8 }}>{t('tagCombosAttempted')}</div>
                        {bandData.tags.map((tag, tIdx) => (
                          <div key={tIdx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6, padding: '4px 8px', background: '#f8f9fa', borderRadius: 4, alignItems: 'center' }}>
                            <span style={{ color: '#333', flex: 1, fontWeight: 500 }}>{getComboMeaning(tag.combo)}</span>
                            <span style={{ color: getScoreColor(parseFloat(tag.accuracy)), fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 10 }}>{tag.accuracy}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <h3 style={{ marginBottom: 15 }}>{t('uniqueTagCombos')}</h3>
              <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: '400px' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: 12, textAlign: 'left' }}>{t('tagCombination')}</th>
                      <th style={{ padding: 12, textAlign: 'center', width: '120px' }}>{t('accuracy')}</th>
                      <th style={{ padding: 12, textAlign: 'center', width: '150px' }}>{t('performance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const allCombos = {};
                      englishBandData.forEach(b => {
                        b.tags.forEach(t => {
                          if (!allCombos[t.combo]) allCombos[t.combo] = { correct: 0, total: 0 };
                          allCombos[t.combo].correct += t.correct;
                          allCombos[t.combo].total += t.total;
                        });
                      });
                      const sortedCombos = Object.entries(allCombos)
                        .map(([combo, stats]) => {
                          const accuracy = stats.total > 0 ? ((stats.correct / stats.total) * 100).toFixed(1) : 0;
                          return { combo, stats, accuracy: parseFloat(accuracy) };
                        })
                        .sort((a, b) => b.accuracy - a.accuracy);

                      return sortedCombos.map(({ combo, stats, accuracy }) => {
                        const color = getScoreColor(accuracy);
                        const displayText = getComboMeaning(combo);
                        return (
                          <tr key={combo} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: 12, fontWeight: 500 }}>{displayText}</td>
                            <td style={{ padding: 12, textAlign: 'center', fontWeight: 'bold', color: color }}>{accuracy}%</td>
                            <td style={{ padding: 12 }}>
                              <div style={{ width: '100%', height: 10, background: '#e9ecef', borderRadius: 5, overflow: 'hidden' }}>
                                <div style={{ width: `${accuracy}%`, height: '100%', background: color, transition: 'width 0.3s ease' }}></div>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {simpleStats && simpleStats.length > 0 && (
            <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
              <h3 style={{ marginTop: 0, marginBottom: 15, color: '#333' }}>{t('performanceDashboard')} {isScience ? t('byConcept') : t('byBand')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 15 }}>
                {simpleStats.map(stat => {
                  const color = getScoreColor(stat.percentage);
                  return (
                    <div key={stat.category} style={{ padding: 15, background: '#f8f9fa', borderRadius: 6, borderLeft: `5px solid ${color}`, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                      <div style={{ fontSize: 13, color: '#555', marginBottom: 8, fontWeight: 600, textTransform: 'capitalize' }}>{stat.category}</div>
                      <div style={{ fontSize: 28, fontWeight: 'bold', color: color, lineHeight: 1 }}>{stat.percentage}%</div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 5 }}>{stat.correct} / {stat.total} {t('correct')}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
            <h3 style={{ marginTop: 0, fontSize: 16, marginBottom: 15 }}>{t('questionTagAnalysis')}</h3>
            {englishTagStats && englishTagStats.length > 0 ? (
              <>
                <p style={{ fontSize: 13, color: '#666', marginBottom: 15 }}>Performance breakdown by question tags. Sorted from weakest to strongest.</p>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                        <th style={{ padding: 10, textAlign: 'left' }}>{t('tag')}</th>
                        <th style={{ padding: 10, textAlign: 'center', width: '120px' }}>{t('accuracy')}</th>
                        <th style={{ padding: 10, textAlign: 'center', width: '150px' }}>{t('performance')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {englishTagStats.map((stat) => {
                        const color = getScoreColor(stat.percentage);
                        const meaning = getTagMeaning(stat.tag, 'question');
                        const displayText = meaning !== stat.tag ? meaning : stat.tag;
                        return (
                          <tr key={stat.tag} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: 10, fontWeight: 500 }}>{displayText}</td>
                            <td style={{ padding: 10, textAlign: 'center', fontWeight: 'bold', color }}>{stat.percentage}%</td>
                            <td style={{ padding: 10 }}>
                              <div style={{ width: '100%', height: 8, background: '#e9ecef', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ width: `${stat.percentage}%`, height: '100%', background: color }}></div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p style={{ color: '#666', fontStyle: 'italic', padding: '10px 0' }}>No question tags found for this exam.</p>
            )}
          </div>

          <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
            <h3 style={{ marginTop: 0, color: '#343a40' }}>{t('questionAudit', { count: answers.length })}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {answers.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>{t('noAnswers')}</div>
              ) : (
                answers.map((ans, idx) => {
                  const isExpanded = expandedQ === ans.id;
                  return (
                    <div key={ans.id || idx} style={{ border: `1px solid ${ans.is_correct ? '#28a745' : '#dc3545'}`, borderRadius: 6, overflow: 'hidden' }}>
                      <div onClick={() => setExpandedQ(isExpanded ? null : ans.id)} style={{ padding: 12, background: '#f8f9fa', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: 15, alignItems: 'center', fontSize: 14, flexWrap: 'wrap' }}>
                          <strong>Q{idx + 1}</strong>
                          {isMath && <span style={{ fontSize: 12, color: '#666' }}>{ans.metadata?.chapter} - {ans.metadata?.concept}</span>}
                          {isScience && <span style={{ fontSize: 12, color: '#666' }}>{ans.metadata?.chapter_name} - {ans.metadata?.concept}</span>}
                          {isEnglish && <span style={{ fontSize: 12, color: '#666' }}>{t('band')}: {ans.metadata?.band}</span>}
                        </div>
                        <span style={{ color: ans.is_correct ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>{ans.is_correct ? '✓ Correct' : `✗ ${t('wrong')}`} {isExpanded ? '▲' : '▼'}</span>
                      </div>
                      {isExpanded && (
                        <div style={{ padding: 15, background: '#fff' }}>
                          <div style={{ marginBottom: 15, fontSize: 15 }}><MathText>{ans.question_text}</MathText></div>
                          {ans.image_url && <img src={getImageUrl(ans.image_url)} alt="Q" style={{ maxWidth: '100%', maxHeight: 200, marginBottom: 15, borderRadius: 6, border: '1px solid #ddd' }} onError={(e) => { e.target.style.display = 'none'; }} />}
                          <div style={{ display: 'grid', gap: 8, marginBottom: 15 }}>
                            {['A', 'B', 'C', 'D'].map(opt => {
                              if (!ans.options?.[opt]) return null;
                              const isC = ans.correct_answer === opt;
                              const isS = ans.selected_answer === opt;
                              return (
                                <div key={opt} style={{ padding: 10, background: isC ? '#d4edda' : isS ? '#f8d7da' : '#f8f9fa', border: `1px solid ${isC ? '#28a745' : isS ? '#dc3545' : '#ddd'}`, borderRadius: 4, display: 'flex', gap: 10 }}>
                                  <strong>{opt}.</strong> <span style={{flex: 1}}><MathText>{ans.options[opt]}</MathText></span>
                                  {isC && <span style={{color:'#28a745', fontWeight:'bold'}}>✓</span>}
                                  {isS && !isC && <span style={{color:'#dc3545', fontWeight:'bold'}}>✗</span>}
                                </div>
                              );
                            })}
                          </div>
                          <div style={{ fontSize: 12, color: '#666', borderTop: '1px solid #eee', paddingTop: 10 }}>
                            {isMath && (
                              <> 
                                <strong>{t('difficulty')}:</strong> {ans.metadata?.difficulty} | <strong>{t('type')}:</strong> {ans.metadata?.concept_type} 
                                {ans.tags?.length > 0 && <div style={{marginTop: 5}}><strong>{t('tags')}:</strong> {ans.tags.map(t => `${t} (${getTagMeaning(t, 'question')})`).join(', ')}</div>} 
                                {ans.error_tags?.length > 0 && <div style={{marginTop: 5, color: '#dc3545'}}><strong>{t('misconceptionsSmall')}:</strong> {ans.error_tags.map(t => `${t} (${getTagMeaning(t, 'error')})`).join(', ')}</div>} 
                              </>
                            )}
                            {isScience && (
                              <> 
                                <strong>{t('subConcept')}:</strong> {ans.metadata?.sub_concept} | <strong>{t('type')}:</strong> {ans.metadata?.concept_type} 
                                {ans.skillset_tags?.length > 0 && <div style={{marginTop: 5}}><strong>{t('skillsets')}:</strong> {ans.skillset_tags.join(', ')}</div>} 
                                {ans.error_tags?.length > 0 && !ans.is_correct && <div style={{marginTop: 5, color: '#dc3545'}}><strong>{t('misconceptionsSmall')}:</strong> {ans.error_tags.map(t => `${t} (${getTagMeaning(t, 'error')})`).join(', ')}</div>} 
                              </>
                            )}
                            {isEnglish && (
                              <> 
                                <strong>{t('band')}:</strong> {ans.metadata?.band} 
                                {ans.tags?.length > 0 && <div style={{marginTop: 5}}><strong>{t('tags')}:</strong> {ans.tags.map(t => `${t} - ${getTagMeaning(t, 'question')}`).join(', ')}</div>} 
                                {ans.error_tags?.length > 0 && !ans.is_correct && <div style={{marginTop: 5, color: '#dc3545'}}><strong>{t('misconceptionsSmall')}:</strong> {ans.error_tags.map(t => `${t} - ${getTagMeaning(t, 'error')}`).join(', ')}</div>} 
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}